import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Brain } from 'lucide-react';
export default function Neurodevelopmental() {
  return <main className="min-h-screen bg-white" data-id="element-1238">
      <Helmet data-id="element-1239">
        <title data-id="element-1240">ADHD & Autism Medico-Legal Assessments | Expert Reports</title>
        <meta name="description" content="Comprehensive ADHD and autism spectrum assessments for court proceedings. Expert medico-legal reports on neurodevelopmental conditions by Dr Imtiaz Syed." data-id="element-1241" />
        <meta name="keywords" content="ADHD & Autism Medico-Legal Assessments, ADHD Assessment, Autism Evaluation, Neurodevelopmental Disorders, Expert Witness, Legal Capacity, ADHD medico-legal reports for court cases UK" data-id="element-1242" />
        <script type="application/ld+json" data-id="element-1243">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "ADHD & Autism Medico-Legal Assessments",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Neurodevelopmental Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert ADHD & Autism Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1244">
        <div className="container mx-auto px-4" data-id="element-1245">
          <div className="flex items-center justify-center mb-4" data-id="element-1246">
            <Brain className="h-12 w-12 mr-4" data-id="element-1247" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-1248">
              ADHD & Autism Medico-Legal Assessments
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1249">
            Specialized evaluations of ADHD, Autism, and their legal
            implications
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1250">
        <div className="container mx-auto px-4" data-id="element-1251">
          <div className="max-w-3xl mx-auto" data-id="element-1252">
            <div className="prose max-w-none" data-id="element-1253">
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1254">
                Expert Neurodevelopmental Evaluations
              </h2>
              <p className="text-slate-dark mb-6" data-id="element-1255">
                Dr. Syed provides comprehensive assessments of
                neurodevelopmental conditions, with a particular focus on
                Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism
                Spectrum Disorder (ASD). These specialized evaluations are
                crucial for understanding how these conditions may impact an
                individual's legal context, decision-making capacity, or
                behavior.
              </p>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1256">
                Our ADHD & Autism Assessment Services Include:
              </h3>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1257">
                <li className="mb-2" data-id="element-1258">
                  <strong data-id="element-1259">ADHD Evaluations:</strong> Comprehensive assessments
                  of attention, impulsivity, and hyperactivity symptoms, with
                  consideration of how these may affect legal capacity,
                  culpability, or compliance with legal requirements.
                </li>
                <li className="mb-2" data-id="element-1260">
                  <strong data-id="element-1261">Autism Spectrum Assessments:</strong> Detailed
                  evaluations of social communication, interaction patterns, and
                  restricted/repetitive behaviors, with analysis of how these
                  features may influence legal understanding and behavior.
                </li>
                <li className="mb-2" data-id="element-1262">
                  <strong data-id="element-1263">Impact on Decision-Making Capacity:</strong> Specific
                  assessments of how neurodevelopmental conditions may affect an
                  individual's capacity to make decisions in various legal
                  contexts.
                </li>
                <li className="mb-2" data-id="element-1264">
                  <strong data-id="element-1265">Behavioral Analysis:</strong> Expert interpretation of
                  how neurodevelopmental features may have influenced past
                  behaviors relevant to legal proceedings.
                </li>
                <li data-id="element-1266">
                  <strong data-id="element-1267">Reasonable Adjustments:</strong> Recommendations for
                  appropriate accommodations in legal settings to ensure fair
                  treatment of individuals with neurodevelopmental conditions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1268">
                Our Methodology
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1269">
                Dr. Syed's neurodevelopmental assessments are characterized by:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1270">
                <li className="mb-2" data-id="element-1271">
                  <strong data-id="element-1272">Comprehensive Approach:</strong> Detailed clinical
                  interviews, standardized assessments, and consideration of
                  developmental history.
                </li>
                <li className="mb-2" data-id="element-1273">
                  <strong data-id="element-1274">Contextual Understanding:</strong> Careful analysis of
                  how neurodevelopmental features manifest in specific
                  situations relevant to the legal context.
                </li>
                <li className="mb-2" data-id="element-1275">
                  <strong data-id="element-1276">Differential Diagnosis:</strong> Consideration of
                  other conditions that may mimic or co-occur with ADHD or ASD.
                </li>
                <li data-id="element-1277">
                  <strong data-id="element-1278">Legal Relevance:</strong> Clear articulation of how
                  identified neurodevelopmental features relate to specific
                  legal questions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1279">
                When to Request an ADHD or Autism Assessment
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1280">
                These evaluations are particularly valuable in:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1281">
                <li className="mb-2" data-id="element-1282">
                  Criminal proceedings where ADHD or autism may have influenced
                  behavior
                </li>
                <li className="mb-2" data-id="element-1283">
                  Capacity assessments where neurodevelopmental conditions may
                  affect decision-making
                </li>
                <li className="mb-2" data-id="element-1284">
                  Family court proceedings involving parents or children with
                  neurodevelopmental conditions
                </li>
                <li className="mb-2" data-id="element-1285">
                  Employment tribunal cases related to disability discrimination
                </li>
                <li data-id="element-1286">
                  Immigration cases where vulnerability due to
                  neurodevelopmental conditions is relevant
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1287">
                Expert Court Testimony
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1288">
                Dr. Syed provides clear, accessible expert testimony on
                neurodevelopmental conditions, helping courts and tribunals
                understand the complex ways these conditions may influence
                behavior, capacity, and legal responsibility.
              </p>

              {/* Related Services */}
              <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1289">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1290">
                  Explore Our Other Services
                </h3>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1291">
                  <Link to="/services/mental-capacity" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1292">
                    Mental Capacity Assessments
                  </Link>
                  <Link to="/services/forensic-risk" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1293">
                    Forensic & Risk Assessments
                  </Link>
                  <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1294">
                    Psychiatric Reports for Courts & Tribunals
                  </Link>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 shadow-md mb-8" data-id="element-1295">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1296">
                  Ready to Instruct Dr. Syed?
                </h3>
                <p className="text-slate-dark mb-4" data-id="element-1297">
                  For neurodevelopmental assessments that provide crucial
                  insights for legal proceedings, contact us today to discuss
                  your case requirements.
                </p>
                <Button variant="primary" href="/contact" data-id="element-1298">
                  Book an ADHD or Autism Assessment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}